import type { ScanRequest, ScanResponse } from '@pl/domain';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

export const DB_NAME = 'pl-scan';
export const STORE = 'scans';

/**
 * One scan, as it lives on the phone. The row is written *before* the request goes out, so a
 * phone that dies mid-scan still has it on the next boot. `clientScanId` is generated when the
 * code is captured and never regenerated: every retry carries the same id, which is what lets
 * the server replay the original answer instead of receiving the box twice.
 */
export interface QueuedScan {
  clientScanId: string;
  request: ScanRequest;
  /** Which job the scan belongs to — 'receive', 'ship:<orderId>' — so a screen can count its own. */
  context: string;
  status: 'pending' | 'done' | 'failed';
  response: ScanResponse | null;
  error: string | null;
  attempts: number;
  /**
   * Of those attempts, the ones the server answered for — a 5xx or an unexpected throw. A phone
   * with no signal never raises this, so "the road is out" can never be mistaken for "this scan
   * is poison".
   */
  hardFailures: number;
  createdAt: number;
  /** A bad answer that landed after the worker had already been told the scan was safe. */
  needsAttention: boolean;
}

interface ScanDb extends DBSchema {
  [STORE]: { key: string; value: QueuedScan };
}

let connection: Promise<IDBPDatabase<ScanDb>> | null = null;

/**
 * IndexedDB does not only fail, it hangs: a blocked `deleteDatabase`, a stuck versionchange or
 * one of Safari's long-standing wedges leaves `open` pending forever. Every call is therefore
 * raced against a clock. Without it `submit` awaits its write and never returns, and the worker
 * is left holding a box in front of a screen that says nothing at all — the one outcome this
 * queue exists to make impossible. Losing durability is survivable; losing the scan is not.
 */
let timeoutMs = 2500;
/** After a wedge, storage is skipped outright for a while rather than costing every scan 2.5 s. */
let disabledUntil = 0;
const BACKOFF_MS = 30_000;

class StorageUnavailable extends Error {
  constructor(why: string) {
    super(`Scan storage is unavailable (${why})`);
  }
}

async function guard<T>(what: string, run: (instance: IDBPDatabase<ScanDb>) => Promise<T>): Promise<T> {
  if (Date.now() < disabledUntil) throw new StorageUnavailable('recently wedged');
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => run(await db()))(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new StorageUnavailable(`${what} timed out`)), timeoutMs);
      }),
    ]);
  } catch (err) {
    disabledUntil = Date.now() + BACKOFF_MS;
    // A wedged or dead connection must not stay cached, or it poisons every later call.
    connection = null;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

function db(): Promise<IDBPDatabase<ScanDb>> {
  if (!connection) {
    connection = openDB<ScanDb>(DB_NAME, 1, {
      upgrade(instance) {
        if (!instance.objectStoreNames.contains(STORE)) instance.createObjectStore(STORE, { keyPath: 'clientScanId' });
      },
    });
    // A rejected connection must not be cached, or every later call fails against the same
    // dead promise even once the storage is usable again.
    connection.catch(() => {
      connection = null;
    });
  }
  return connection;
}

export async function loadScans(): Promise<QueuedScan[]> {
  return guard('read', (instance) => instance.getAll(STORE));
}

export async function saveScan(item: QueuedScan): Promise<void> {
  await guard('write', (instance) => instance.put(STORE, item));
}

export async function removeScan(clientScanId: string): Promise<void> {
  await guard('delete', (instance) => instance.delete(STORE, clientScanId));
}

/** Test seam: the real 2.5 s clock would make a wedged-storage test take 2.5 s. */
export function __setStorageTimeoutForTests(ms: number): void {
  timeoutMs = ms;
}

/** Closes the cached connection and drops the database, so each test starts on a fresh phone. */
export async function __resetForTests(): Promise<void> {
  const open = connection;
  connection = null;
  disabledUntil = 0;
  timeoutMs = 2500;
  if (open) await open.then((instance) => instance.close()).catch(() => undefined);
  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = request.onerror = request.onblocked = () => resolve();
  });
}
